import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  People as PeopleIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import UserList from '../components/lists/UserList';
import UserForm from '../components/forms/UserForm';
import UserDetails from '../components/details/UserDetails';
import Modal from '../components/common/Modal';
import apiService from '../services/api';
import { pageVariants, sectionVariants } from '../animations/variants';

const Users = ({
  groups = [],
  fetchAllGroups,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  fetchGroupMembers,
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });
  const searchTimeoutRef = useRef(null);
  const dataFetchedRef = useRef(false);
  const location = useLocation();

  // Fetch users with pagination
  const fetchUsers = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await apiService.getAllUsers({
        page: pagination.page + 1, // API uses 1-indexed pages
        pageSize: pagination.pageSize,
        search: searchQuery || undefined,
      });
      if (response.success && response.data) {
        setUsers(response.data.users || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 0,
        }));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [pagination.page, pagination.pageSize, searchQuery]);

  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPagination(prev => ({ ...prev, page: 0 }));
    }, 400);
  };

  useEffect(() => {
    if (location.state) {
      if (location.state.showForm) {
        setShowForm(true);
      }
      // Handle navigation from permission set assignments page
      if (location.state.selectUser) {
        handleUserClick(location.state.selectUser);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (!dataFetchedRef.current) {
      if (groups.length === 0) fetchAllGroups();
      dataFetchedRef.current = true;
    }
  }, [fetchAllGroups, groups.length]);

  const handleRefresh = async () => {
    await fetchUsers();
  };

  const handleUserClick = async (username) => {
    const userData = await getUser(username);

    if (userData) {
      setSelectedUser({
        ...userData,
        groups: [],
        isLoadingGroups: true,
        assignments: [],
        applicationAssignments: [],
        isLoadingAssignments: true
      });

      try {
        const response = await apiService.getUserEffectiveAssignments(username);
        if (response.success && response.data) {
          setSelectedUser(prev => ({
            ...prev,
            groups: response.data.groups || [],
            isLoadingGroups: false,
            assignments: response.data.assignments || [],
            applicationAssignments: response.data.applicationAssignments || [],
            isLoadingAssignments: false
          }));
          return;
        }
      } catch (error) {
        console.error('Error fetching effective assignments:', error);
      }

      // Fallback: fetch groups the old way if the new endpoint fails
      const userGroups = [];

      if (fetchGroupMembers && groups.length > 0) {
        try {
          const membershipChecks = groups.map(async (group) => {
            try {
              const members = await fetchGroupMembers(group.name);
              if (members && members.some(member => member.username === username)) {
                return group.name;
              }
              return null;
            } catch (error) {
              console.error(`Error checking membership for group ${group.name}:`, error);
              return null;
            }
          });

          const results = await Promise.all(membershipChecks);
          userGroups.push(...results.filter(groupName => groupName !== null));
        } catch (error) {
          console.error('Error checking group memberships:', error);
        }
      }

      setSelectedUser(prev => ({
        ...prev,
        groups: userGroups,
        isLoadingGroups: false,
        assignments: [],
        applicationAssignments: [],
        isLoadingAssignments: false
      }));
    }
  };

  const handleUserUpdate = async (username, userData) => {
    const success = await updateUser(username, userData);
    if (success) {
      setSelectedUser(null);
    }
  };

  const handleUserDelete = async (username) => {
    const success = await deleteUser(username);
    if (success) {
      setSelectedUser(null);
      fetchUsers();
    }
  };

  const handleCreateUser = async (userData) => {
    const success = await createUser(userData);
    if (success) {
      setShowForm(false);
      fetchUsers(); // Refresh the list
    }
    return success;
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Page Header */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#4698D3',
                width: 48,
                height: 48,
              }}
            >
              <PeopleIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                Users
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                  fontWeight: 500,
                }}
              >
                {pagination.total} user{pagination.total !== 1 ? 's' : ''} in the system
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search users..."
              value={searchInput}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 250,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F9FBFD',
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  '& fieldset': {
                    borderColor: '#E5E5E5',
                  },
                  '&:hover fieldset': {
                    borderColor: '#4698D3',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4698D3',
                  },
                },
              }}
            />
            <Tooltip title="Refresh Users">
              <IconButton
                onClick={handleRefresh}
                disabled={isRefreshing}
                sx={{
                  color: '#666666',
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#F0F4F8',
                    borderColor: '#4698D3',
                    color: '#4698D3',
                  },
                  '&:disabled': {
                    color: '#BDBDBD',
                    backgroundColor: '#F9FBFD',
                  },
                }}
              >
                {isRefreshing ? (
                  <CircularProgress size={20} sx={{ color: '#4698D3' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowForm(true)}
              sx={{
                backgroundColor: '#4698D3',
                borderRadius: '6px',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                '&:hover': {
                  backgroundColor: '#3580B8',
                },
              }}
            >
              Create User
            </Button>
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Users List */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          overflow: 'hidden',
          minHeight: '400px',
        }}
      >
        <UserList users={users} onUserClick={handleUserClick} loading={isRefreshing} />

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          rowsPerPage={pagination.pageSize}
          onRowsPerPageChange={(e) =>
            setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))
          }
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: '1px solid #E5E5E5',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontFamily: 'Metropolis, sans-serif',
            },
          }}
        />
      </Paper>
      </motion.div>

      {/* Modals */}
      <AnimatePresence>
        {showForm && (
          <Modal onClose={() => setShowForm(false)} key="user-form-modal">
            <UserForm
              groups={groups}
              onSubmit={handleCreateUser}
              onCancel={() => setShowForm(false)}
            />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedUser && (
          <Modal onClose={() => setSelectedUser(null)} key="user-details-modal">
            <UserDetails
              user={selectedUser}
              onUpdate={handleUserUpdate}
              onDelete={handleUserDelete}
              onClose={() => setSelectedUser(null)}
              onRevokeComplete={() => handleUserClick(selectedUser.username)}
            />
          </Modal>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Users;
